import { useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import {
  Card,
  Chip,
  Select,
  MenuItem,
  useTheme,
  TextField,
  CardHeader,
  Typography,
  IconButton,
  CardContent,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { NoteType, NoteTypeStatus, NoteTypeScopeType } from 'src/services/organisation/organisation.types';
import {
  useCreateNoteTypeMutation,
  useUpdateNoteTypeMutation,
  useArchiveNoteTypeMutation,
  useRestoreNoteTypeMutation,
  useGetOrganizationNoteTypesQuery,
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

/** Mirrors NoteTypesService.MAX_CUSTOM_NOTE_TYPES. The server is what enforces it. */
export const MAX_CUSTOM_NOTE_TYPES = 15;

const DUPLICATE_NAME_ERROR = '26_0002';
const LIMIT_REACHED_ERROR = '26_0001';

const ICON_CHOICES = [
  'fluent:note-20-regular',
  'mage:phone-outgoing',
  'mage:phone-incoming',
  'famicons:calendar-outline',
  'eva:email-outline',
  'eva:message-square-outline',
  'eva:people-outline',
  'eva:checkmark-circle-2-outline',
  'eva:clock-outline',
  'eva:star-outline',
  'eva:briefcase-outline',
  'eva:file-text-outline',
];

const COLOUR_CHOICES = [
  '#637381',
  '#00A76F',
  '#00B8D9',
  '#8E33FF',
  '#FFAB00',
  '#FF5630',
  '#22C55E',
  '#006C9C',
];

/**
 * Manage the organisation's note types (JT1-1595). The four types JobTetris ships are shown but not
 * editable - they are shared by every organisation - while the organisation's own can be added,
 * renamed, restyled and archived.
 *
 * Archiving, not deleting: a note holds exactly one type, so removing the row would rewrite history
 * and change activity reports for periods that have already been read.
 */
export default function NoteTypesSettingsCard() {
  const { t } = useTranslate();
  const theme = useTheme();

  const { account, organization } = useAuthContext();

  const { currentData, isLoading } = useGetOrganizationNoteTypesQuery({
    org_id: organization?.id,
    include_archived: true,
  });

  const [createNoteType, { isLoading: isCreating }] = useCreateNoteTypeMutation();
  const [updateNoteType, { isLoading: isUpdating }] = useUpdateNoteTypeMutation();
  const [archiveNoteType] = useArchiveNoteTypeMutation();
  const [restoreNoteType] = useRestoreNoteTypeMutation();

  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState(ICON_CHOICES[0]);
  const [newColour, setNewColour] = useState(COLOUR_CHOICES[0]);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingIcon, setEditingIcon] = useState(ICON_CHOICES[0]);
  const [editingColour, setEditingColour] = useState(COLOUR_CHOICES[0]);
  const [editError, setEditError] = useState<string | null>(null);

  const noteTypes = useMemo<NoteType[]>(() => currentData ?? [], [currentData]);

  const systemTypes = useMemo(
    () => noteTypes.filter((noteType) => noteType.scope_type === NoteTypeScopeType.SYSTEM),
    [noteTypes]
  );
  const activeTenantTypes = useMemo(
    () => noteTypes.filter((noteType) =>
      noteType.scope_type === NoteTypeScopeType.TENANT && noteType.status === NoteTypeStatus.ACTIVE),
    [noteTypes]
  );
  const archivedTenantTypes = useMemo(
    () => noteTypes.filter((noteType) =>
      noteType.scope_type === NoteTypeScopeType.TENANT && noteType.status === NoteTypeStatus.ARCHIVED),
    [noteTypes]
  );

  const canEdit = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  // Archived types are free, so only the active ones count against the allowance.
  const remaining = MAX_CUSTOM_NOTE_TYPES - activeTenantTypes.length;
  const atLimit = remaining <= 0;

  const errorKeyFor = (error: any): string => {
    if (error?.error_code === DUPLICATE_NAME_ERROR) return t('admin-organization.settings.note_types.errors.duplicate');
    if (error?.error_code === LIMIT_REACHED_ERROR) return t('admin-organization.settings.note_types.errors.limit');
    return t('admin-organization.settings.note_types.errors.generic');
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;

    setCreateError(null);

    try {
      await createNoteType({
        org_id: organization.id,
        name,
        icon: newIcon,
        colour: newColour,
      }).unwrap();

      setNewName('');
      enqueueSnackbar(t('admin-organization.settings.note_types.api.create.success'), { variant: 'success' });
    } catch (error) {
      // The cap and name uniqueness are the server's rules; show what it said against the field
      // rather than a snackbar the user has to guess at.
      setCreateError(errorKeyFor(error));
    }
  };

  const handleStartEdit = (noteType: NoteType) => {
    setEditingId(noteType.id);
    setEditingName(noteType.name);
    setEditingIcon(noteType.icon);
    setEditingColour(noteType.colour);
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    const name = editingName.trim();
    if (!editingId || !name) return;

    try {
      await updateNoteType({
        org_id: organization.id,
        note_type_id: editingId,
        name,
        icon: editingIcon,
        colour: editingColour,
      }).unwrap();

      handleCancelEdit();
      enqueueSnackbar(t('admin-organization.settings.note_types.api.update.success'), { variant: 'success' });
    } catch (error) {
      setEditError(errorKeyFor(error));
    }
  };

  const handleArchive = async (noteType: NoteType) => {
    try {
      await archiveNoteType({ org_id: organization.id, note_type_id: noteType.id }).unwrap();
      enqueueSnackbar(t('admin-organization.settings.note_types.api.archive.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('admin-organization.settings.note_types.api.archive.default_error'), { variant: 'error' });
    }
  };

  const handleRestore = async (noteType: NoteType) => {
    try {
      await restoreNoteType({ org_id: organization.id, note_type_id: noteType.id }).unwrap();
      enqueueSnackbar(t('admin-organization.settings.note_types.api.restore.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(errorKeyFor(error), { variant: 'error' });
    }
  };

  const renderIconSelect = (value: string, onChange: (icon: string) => void, disabled: boolean) => (
    <Select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      size="small"
      disabled={disabled}
      sx={{ width: 80 }}
      inputProps={{ 'aria-label': t('admin-organization.settings.note_types.labels.icon') }}
    >
      {ICON_CHOICES.map((icon) => (
        <MenuItem key={icon} value={icon}>
          <Iconify icon={icon} />
        </MenuItem>
      ))}
    </Select>
  );

  const renderColourSelect = (value: string, onChange: (colour: string) => void, disabled: boolean) => (
    <Select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      size="small"
      disabled={disabled}
      sx={{ width: 80 }}
      inputProps={{ 'aria-label': t('admin-organization.settings.note_types.labels.colour') }}
    >
      {COLOUR_CHOICES.map((colour) => (
        <MenuItem key={colour} value={colour}>
          <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: colour }} />
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={{ width: '100%', position: 'relative', boxShadow: 0 }}>
        <CardHeader
          avatar={
            <Iconify
              icon="fluent:note-20-regular"
              sx={{ width: 30, height: 30, color: theme.palette.primary.main }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.note_types.title')}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="textSecondary">
              {t('admin-organization.settings.note_types.description')}
            </Typography>
          }
        />

        <CardContent>
          {isLoading ? (
            <Typography variant="body2" color="textSecondary">
              {t('common.loading')}
            </Typography>
          ) : (
            <Stack direction="column" gap={3} sx={{ mt: 1 }}>

              {/* The four JobTetris ships - shared by every organisation, so read-only here */}
              <Stack gap={1}>
                <Typography variant="caption" color="textSecondary">
                  {t('admin-organization.settings.note_types.system_types')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {systemTypes.map((noteType) => (
                    <Chip
                      key={noteType.id}
                      label={noteType.name}
                      size="small"
                      variant="outlined"
                      icon={<Iconify icon={noteType.icon} sx={{ color: `${noteType.colour} !important` }} />}
                    />
                  ))}
                </Box>
              </Stack>

              {/* The organisation's own */}
              <Stack gap={1}>
                <Typography variant="caption" color="textSecondary">
                  {t('admin-organization.settings.note_types.your_types')}
                </Typography>

                {activeTenantTypes.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    {t('admin-organization.settings.note_types.no_custom_types')}
                  </Typography>
                )}

                {activeTenantTypes.map((noteType) => (
                  <Stack key={noteType.id} direction="row" alignItems="center" gap={1}>
                    {editingId === noteType.id ? (
                      <>
                        {renderIconSelect(editingIcon, setEditingIcon, isUpdating)}
                        {renderColourSelect(editingColour, setEditingColour, isUpdating)}
                        <TextField
                          value={editingName}
                          onChange={(event) => setEditingName(event.target.value)}
                          size="small"
                          fullWidth
                          autoFocus
                          error={!!editError}
                          helperText={editError}
                        />
                        <IconButton
                          color="primary"
                          onClick={handleSaveEdit}
                          disabled={isUpdating || !editingName.trim()}
                          aria-label={t('common.save')}
                        >
                          <Iconify icon="eva:checkmark-fill" />
                        </IconButton>
                        <IconButton onClick={handleCancelEdit} aria-label={t('common.cancel')}>
                          <Iconify icon="eva:close-fill" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Iconify icon={noteType.icon} sx={{ color: noteType.colour }} />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {noteType.name}
                        </Typography>
                        <IconButton
                          onClick={() => handleStartEdit(noteType)}
                          disabled={!canEdit}
                          aria-label={t('common.edit')}
                        >
                          <Iconify icon="eva:edit-2-outline" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleArchive(noteType)}
                          disabled={!canEdit}
                          aria-label={t('admin-organization.settings.note_types.archive')}
                        >
                          <Iconify icon="eva:archive-outline" />
                        </IconButton>
                      </>
                    )}
                  </Stack>
                ))}

                {/* Add a new type */}
                <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mt: 1 }}>
                  {renderIconSelect(newIcon, setNewIcon, !canEdit || atLimit)}
                  {renderColourSelect(newColour, setNewColour, !canEdit || atLimit)}
                  <TextField
                    value={newName}
                    onChange={(event) => {
                      setNewName(event.target.value);
                      setCreateError(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleCreate();
                      }
                    }}
                    placeholder={t('admin-organization.settings.note_types.add_placeholder')}
                    size="small"
                    fullWidth
                    disabled={!canEdit || atLimit}
                    error={!!createError}
                    helperText={createError}
                  />
                  <LoadingButton
                    variant="outlined"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={handleCreate}
                    loading={isCreating}
                    disabled={!canEdit || atLimit || !newName.trim()}
                  >
                    {t('admin-organization.settings.note_types.add')}
                  </LoadingButton>
                </Stack>

                <Typography variant="caption" color={atLimit ? 'error' : 'textSecondary'}>
                  {atLimit
                    ? t('admin-organization.settings.note_types.limit_reached')
                    : t('admin-organization.settings.note_types.remaining', { count: remaining })}
                </Typography>
              </Stack>

              {/* Archived - kept so past notes and past reports still read correctly */}
              {archivedTenantTypes.length > 0 && (
                <Stack gap={1}>
                  <Typography variant="caption" color="textSecondary">
                    {t('admin-organization.settings.note_types.archived_types')}
                  </Typography>

                  {archivedTenantTypes.map((noteType) => (
                    <Stack key={noteType.id} direction="row" alignItems="center" gap={1}>
                      <Iconify icon={noteType.icon} sx={{ color: theme.palette.text.disabled }} />
                      <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
                        {noteType.name}
                      </Typography>
                      <IconButton
                        onClick={() => handleRestore(noteType)}
                        disabled={!canEdit || atLimit}
                        aria-label={t('admin-organization.settings.note_types.restore')}
                      >
                        <Iconify icon="eva:undo-outline" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}

            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
